// src/utils/memberQuality.js
// Heuristic data-quality checks for member records, surfaced on the Club
// Dashboard so admins notice odd-looking entries (typos, test rows left in
// by mistake, copy-paste duplicates) shortly after they're added instead of
// months later. These are pattern-matching heuristics, not proof of
// wrongdoing -- callers should present them as "worth a second look," never
// as a confirmed verdict on a real person.

const PLACEHOLDER_NAME_RE = /^(test|asdf|admin|sample|demo|xxx+|n\/?a)\b/i;
const SLIIT_ID_RE = /^[A-Za-z]{2}\d{6,}$/;

function isRepeatedChar(str) {
  return str.length > 0 && new Set(str.toLowerCase().replace(/\s+/g, '')).size === 1;
}

// Returns an array of short, human-readable reason strings; empty means no
// flags were raised. `allSliitIds` is the full roster's ID list, passed in
// by the caller so this stays a pure function instead of re-deriving it
// per member (an O(n^2) trap for a few thousand rows).
export function getMemberQualityFlags(member, allSliitIds) {
  const flags = [];
  const name = (member.FullName || '').trim();
  const sliitId = (member.SLIITID || '').trim();

  if (name) {
    if (PLACEHOLDER_NAME_RE.test(name) || isRepeatedChar(name)) {
      flags.push('Name looks like placeholder/test data');
    }
    if (/\d/.test(name)) {
      flags.push('Name contains digits');
    }
    if (name.length < 3) {
      flags.push('Name is unusually short');
    }
  } else {
    flags.push('Missing full name');
  }

  if (sliitId) {
    if (!SLIIT_ID_RE.test(sliitId)) {
      flags.push('Unusual SLIIT ID format');
    }
    const enrollmentYear = sliitId.match(/^[A-Za-z]{2}(\d{2})/);
    if (enrollmentYear) {
      const year = 2000 + parseInt(enrollmentYear[1], 10);
      if (year > new Date().getFullYear()) {
        flags.push('SLIIT ID implies a future enrollment year');
      }
    }
    if (allSliitIds) {
      const dupeCount = allSliitIds.filter((id) => id && id.trim() === sliitId).length;
      if (dupeCount > 1) flags.push('Duplicate SLIIT ID elsewhere in the roster');
    }
  } else {
    flags.push('Missing SLIIT ID');
  }

  if (member.RegDate) {
    const regDate = new Date(member.RegDate);
    if (!isNaN(regDate.getTime()) && regDate.getTime() > Date.now()) {
      flags.push('Registration date is in the future');
    }
  }

  if (!member.Email && !member.ContactNo && !member.NIC) {
    flags.push('No email, phone, or NIC on file');
  }

  return flags;
}

export function getFlaggedMembers(members) {
  const allSliitIds = members.map((m) => m.SLIITID).filter(Boolean);
  return members
    .map((member) => ({ member, flags: getMemberQualityFlags(member, allSliitIds) }))
    .filter((entry) => entry.flags.length > 0);
}
